import React, { useState, useCallback, useEffect } from 'react';
import { ContextualAIModal, ContextSource, AmbientMessageDisplay } from '../components/Space/ContextualAIModal/ContextualAIModal';
import { createFileLogger } from '../utils/logger';
import { globalShortcutsService } from '../services/globalShortcutsService';
import {
  holdAISelectionHighlight,
  releaseAISelectionHighlight,
  resolveActiveAISelection,
} from '../services/aiSelection';
import type { AmbientSuggestion } from './useAmbientSuggestions';

const log = createFileLogger('GlobalCmdK', { enabled: false });

const MODAL_WIDTH = 500;
const MODAL_ESTIMATED_HEIGHT = 190;
const ANCHOR_GAP = 10;
const VIEWPORT_MARGIN = 16;

/**
 * Cmd+K needs the id of the Space whose SpaceChat will run the prompt.
 * Both route shapes are matched: `/desktops/:id` is where every Space renders
 * today, and `/spaces/:id` still resolves for the standard-chrome escape hatch
 * (?presentation=standard / ?adminView=true).
 */
function getSpaceIdFromUrl(): string | null {
  const match = window.location.pathname.match(/\/(?:spaces|desktops)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

/**
 * Place the modal just above the selection, clamped into the viewport, flipping
 * below when there isn't room above.
 */
function positionForAnchor(
  rect: { top: number; bottom: number; left: number; right: number },
): { x: number; y: number } {
  const centerX = (rect.left + rect.right) / 2 - MODAL_WIDTH / 2;
  const x = Math.min(
    Math.max(centerX, VIEWPORT_MARGIN),
    Math.max(VIEWPORT_MARGIN, window.innerWidth - MODAL_WIDTH - VIEWPORT_MARGIN),
  );

  const above = rect.top - MODAL_ESTIMATED_HEIGHT - ANCHOR_GAP;
  const y = above >= VIEWPORT_MARGIN
    ? above
    : Math.min(rect.bottom + ANCHOR_GAP, window.innerHeight - MODAL_ESTIMATED_HEIGHT - VIEWPORT_MARGIN);

  return { x, y: Math.max(VIEWPORT_MARGIN, y) };
}

/**
 * Host for the Contextual AI modal — the system-level "ask AI about this"
 * surface, mounted once at the App root so it floats over every layout
 * (Desktop spatial/canvas/focused/workspace and standard Space chrome alike).
 *
 * Two entry points:
 *  - Cmd+K with a live in-app selection. The AI selection SDK
 *    (services/aiSelection) resolves whichever registered app surface owns the
 *    selection; with nothing selected the shortcut DECLINES so global entity
 *    search keeps Cmd+K exactly as before.
 *  - openWithAmbientMessage, called by the Ambient Intelligence Surface.
 */
export const useGlobalCmdKShortcut = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<ContextSource>({ type: 'windows', windowIds: [] });
  const [ambientMessage, setAmbientMessage] = useState<AmbientMessageDisplay | undefined>();
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>();
  const [position, setPosition] = useState<{ x: number; y: number } | undefined>();

  useEffect(() => {
    globalShortcutsService.register({
      id: 'contextual_ai_selection',
      key: 'k',
      modifiers: { cmd: true },
      description: 'Ask AI about the current selection',
      // Ahead of global_entity_search (priority 0) on the same combo.
      priority: 100,
      handler: () => {
        const spaceId = getSpaceIdFromUrl();
        if (!spaceId) return false;

        const selection = resolveActiveAISelection();
        if (!selection) return false;

        log.log('[GlobalCmdK] selection captured —', {
          kind: selection.kind,
          resolvedBy: selection.resolvedBy,
          entityName: selection.entityName,
          chars: selection.text.length,
        });

        setAmbientMessage(undefined);
        setInitialPrompt(undefined);
        setContext({ type: 'selection', selection });
        setPosition(selection.anchorRect ? positionForAnchor(selection.anchorRect) : undefined);
        // The modal's textarea is about to take focus and with it the document
        // selection, so ask the source to hold its own highlight until we close.
        holdAISelectionHighlight(selection.sourceId);
        setIsOpen(true);
        return true;
      },
    });

    return () => globalShortcutsService.unregister('contextual_ai_selection');
  }, []);

  const closeModal = useCallback((options?: { restoreFocus?: boolean }) => {
    setIsOpen(false);
    setAmbientMessage(undefined);
    setInitialPrompt(undefined);
    setPosition(undefined);
    releaseAISelectionHighlight(options);
  }, []);

  /**
   * Dismissal (Escape, close button, backdrop) should feel like the modal was
   * never there — hand focus back so the editor repaints its own selection and
   * the user can keep typing where they left off.
   */
  const handleClose = useCallback(() => closeModal({ restoreFocus: true }), [closeModal]);

  // The modal can also disappear with the whole App subtree (navigation, logout)
  // without either close path running — don't strand a highlight in the editor.
  useEffect(() => () => releaseAISelectionHighlight(), []);

  /**
   * Open the ContextualAIModal programmatically with a synthesized AI message and suggestion cards.
   * Called by the Ambient Intelligence Surface when the user accepts a suggestion.
   * If no Space is active (no spaceId in URL), the modal cannot submit — logs a warning.
   */
  const openWithAmbientMessage = useCallback((suggestion: AmbientSuggestion) => {
    const spaceId = getSpaceIdFromUrl();
    if (!spaceId) {
      // No Space is active — the submission path requires a Space.tsx listener.
      // TODO: Fall back to opening Integrations settings directly when no Space is open.
      log.warn('[GlobalCmdK] openWithAmbientMessage — no spaceId in URL, cannot open modal');
      return;
    }
    log.info('[GlobalCmdK] openWithAmbientMessage', {
      headline: suggestion.headline,
      cards: suggestion.cards.length,
    });
    setAmbientMessage({ messageText: suggestion.modalMessage, cards: suggestion.cards });
    setInitialPrompt(suggestion.prefilledPrompt);
    setContext(suggestion.context);
    setPosition(undefined);
    setIsOpen(true);
  }, []);

  const handleSubmit = useCallback((prompt: string, ctx: ContextSource) => {
    const spaceId = getSpaceIdFromUrl();
    if (!spaceId) {
      log.warn('[GlobalCmdK] Submit called but no spaceId in URL — ignoring');
      closeModal();
      return;
    }

    log.log('[GlobalCmdK] Submitting prompt — spaceId:', spaceId, 'prompt:', prompt.slice(0, 80));

    // Communicate with the Space instance via a DOM event.
    // Space.tsx listens for this event and calls handleContextualAISubmit.
    window.dispatchEvent(
      new CustomEvent('river:contextual-ai-submit', {
        detail: { prompt, context: ctx, spaceId },
      })
    );

    // No restoreFocus: the prompt is on its way to SpaceChat, which takes focus.
    closeModal();
  }, [closeModal]);

  const GlobalCmdKModal = isOpen ? (
    <ContextualAIModal
      isOpen={isOpen}
      onClose={handleClose}
      context={context}
      onSubmit={handleSubmit}
      ambientMessage={ambientMessage}
      initialPrompt={initialPrompt}
      position={position}
    />
  ) : null;

  return {
    GlobalCmdKModal,
    openCmdK: () => setIsOpen(true),
    closeCmdK: handleClose,
    isOpen,
    openWithAmbientMessage,
  };
};
